import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Checkbox,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Accordion,
  AccordionItem,
  InlineLoading,
} from "@carbon/react";

// Constants
const ROOM_TYPE_ORDER = ["Single", "Double", "Three", "Four"];
const FIELD_TO_ATTRIBUTE_MAP = {
  roomAvailableRoomTypes: "room_available_room_types",
  roomExpectedRent: "room_expected_rent",
  roomExpectedDeposit: "room_expected_deposit",
  roomExpectedRentPerPerson: "room_expected_rent_per_person",
  roomExpectedDepositPerPerson: "room_expected_deposit_per_person",
  roomExpectedRentThree: "room_expected_rent_three",
  roomExpectedDepositThree: "room_expected_deposit_three",
  roomExpectedRentFour: "room_expected_rent_four",
  roomExpectedDepositFour: "room_expected_deposit_four",
  roomAmenities: "room_amenities"
};

const RoomDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 2,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  CommonButtons,
  onNavigateNext,
}) => {
  

  // const isEditMode = useMemo(() => 
  //   formMode === "edit" || lastSavedStep >= currentStep,
  //   [formMode, lastSavedStep, currentStep]
  // );

  //  STATE MANAGEMENT 
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");          // 👈 ADD
const [toastKey, setToastKey] = useState(0); 
  const [touched, setTouched] = useState({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [form, setForm] = useState({
    selectedRoomTypes: [],
    roomDetails: {},
  });

  //  UTILITY FUNCTIONS 
  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getRoomTypeOrder = useCallback((roomTypeName) => {
    const index = ROOM_TYPE_ORDER.findIndex(type => 
      roomTypeName?.toLowerCase().includes(type.toLowerCase())
    );
    return index === -1 ? ROOM_TYPE_ORDER.length : index;
  }, []);

  //  DATA PROCESSING 
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey;
      const match = normalize(apiKey);
      
      const filtered = attributeValues.filter((it) => {
        const isActive = it.isActive === true || it.status === "Active";
        const attrName = it.attrName || it.attr_name;
        const matchesAttrName = normalize(attrName) === match;
        
        return isActive && matchesAttrName;
      });
      
      return filtered;
    },
    [attributeValues, normalize]
  );

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  // Get room types from attribute values
  const roomTypes = useMemo(() => {
    const options = getOptions("roomAvailableRoomTypes");
    
    if (!options || options.length === 0) {
      return [];
    }
    
    return options
      .map(attr => ({
        id: String(attr.value || attr.id),
        name: attr.label,
        label: attr.label,
        order: getRoomTypeOrder(attr.label)
      }))
      .filter(rt => rt.id && rt.name)
      .sort((a, b) => a.order - b.order);
  }, [getOptions, getRoomTypeOrder]);

  // Get amenities options
  const amenitiesOptions = useMemo(() => {
    const options = getOptions("roomAmenities");
    
    if (!options || options.length === 0) {
      return [];
    }
    
    return options.map(attr => ({
      id: String(attr.value || attr.id),
      name: attr.label,
      label: attr.label
    })).filter(amenity => amenity.id && amenity.name);
  }, [getOptions]);

  // ============== VALIDATION ==============
  const validationRules = useMemo(
    () => ({
      selectedRoomTypes: {
        required: true,
        message: "Please select at least one room type"
      }
    }),
    []
  );

  const validateField = useCallback((roomTypeId, fieldName, value) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    if (!roomType) return null;

    const roomName = roomType.name.toLowerCase();
    
    if (roomName.includes("single")) {
      if (fieldName === 'rent' && (!value || Number(value) <= 0)) {
        return "Expected Rent is required and must be greater than 0";
      }
      if (fieldName === 'deposit' && (!value || Number(value) <= 0)) {
        return "Expected Deposit is required and must be greater than 0";
      }
    } else if (roomName.includes("double") || roomName.includes("three") || roomName.includes("four")) {
      if (fieldName === 'rentPerPerson' && (!value || Number(value) <= 0)) {
        return "Expected Rent per person is required and must be greater than 0";
      }
      if (fieldName === 'depositPerPerson' && (!value || Number(value) <= 0)) {
        return "Expected Deposit per person is required and must be greater than 0";
      }
      if (roomName.includes("three")) {
        if (fieldName === 'rentThree' && (!value || Number(value) <= 0)) {
          return "Expected Rent is required and must be greater than 0";
        }
        if (fieldName === 'depositThree' && (!value || Number(value) <= 0)) {
          return "Expected Deposit is required and must be greater than 0";
        }
      }
      if (roomName.includes("four")) {
        if (fieldName === 'rentFour' && (!value || Number(value) <= 0)) {
          return "Expected Rent is required and must be greater than 0";
        }
        if (fieldName === 'depositFour' && (!value || Number(value) <= 0)) {
          return "Expected Deposit is required and must be greater than 0";
        }
      }
    }
    return null;
  }, [roomTypes]);

  const getFieldError = useCallback(
    (field) => {
      if (field === "selectedRoomTypes") {
        if (!form.selectedRoomTypes?.length) {
          return validationRules.selectedRoomTypes.message;
        }
        return null;
      }

      if (field && field.includes('.')) {
        const [roomTypeId, fieldName] = field.split('.');
        const value = form.roomDetails[roomTypeId]?.[fieldName];
        return validateField(roomTypeId, fieldName, value);
      }

      return null;
    },
    [form, validationRules, validateField]
  );

  const validateForm = useCallback(() => {
    if (!form.selectedRoomTypes?.length) {
      return validationRules.selectedRoomTypes.message;
    }

    for (const roomTypeId of form.selectedRoomTypes) {
      const details = form.roomDetails[roomTypeId] || {};
      const roomType = roomTypes.find(rt => rt.id === roomTypeId);
      
      if (!roomType) continue;

      const roomName = roomType.name.toLowerCase();

      if (roomName.includes("single")) {
        if (!details.rent || Number(details.rent) <= 0) {
          return `${roomType.name}: Expected Rent is required and must be greater than 0`;
        }
        if (!details.deposit || Number(details.deposit) <= 0) {
          return `${roomType.name}: Expected Deposit is required and must be greater than 0`;
        }
      } else if (roomName.includes("double")) {
        if (!details.rentPerPerson || Number(details.rentPerPerson) <= 0) {
          return `${roomType.name}: Expected Rent per person is required and must be greater than 0`;
        }
        if (!details.depositPerPerson || Number(details.depositPerPerson) <= 0) {
          return `${roomType.name}: Expected Deposit per person is required and must be greater than 0`;
        }
      } else if (roomName.includes("three")) {
        if (!details.rentThree || Number(details.rentThree) <= 0) {
          return `${roomType.name}: Expected Rent is required and must be greater than 0`;
        }
        if (!details.depositThree || Number(details.depositThree) <= 0) {
          return `${roomType.name}: Expected Deposit is required and must be greater than 0`;
        }
      } else if (roomName.includes("four")) {
        if (!details.rentFour || Number(details.rentFour) <= 0) {
          return `${roomType.name}: Expected Rent is required and must be greater than 0`;
        }
        if (!details.depositFour || Number(details.depositFour) <= 0) {
          return `${roomType.name}: Expected Deposit is required and must be greater than 0`;
        }
      }
    }

    return null;
  }, [form, roomTypes, validationRules]);

  // ============== FORM HANDLERS ==============
  const handleBlur = useCallback((f) => {
    setTouched((prev) => ({ ...prev, [f]: true }));
  }, []);

  const toggleRoomType = useCallback((roomTypeId) => {
    setForm(prev => {
      const currentSelected = prev.selectedRoomTypes || [];
      const isSelected = currentSelected.includes(roomTypeId);
      
      if (isSelected) {
        const { [roomTypeId]: removed, ...remainingDetails } = prev.roomDetails;
        return {
          selectedRoomTypes: currentSelected.filter(id => id !== roomTypeId),
          roomDetails: remainingDetails
        };
      } else {
        const roomType = roomTypes.find(rt => rt.id === roomTypeId);
        const roomName = roomType?.name?.toLowerCase() || "";
        let initialDetails = { amenities: [] };

        if (roomName.includes("single")) {
          initialDetails = {
            rent: "",
            deposit: "",
            amenities: []
          };
        } else if (roomName.includes("double")) {
          initialDetails = {
            rentPerPerson: "",
            depositPerPerson: "",
            amenities: []
          };
        } else if (roomName.includes("three")) {
          initialDetails = {
            rentThree: "",
            depositThree: "",
            amenities: []
          };
        } else if (roomName.includes("four")) {
          initialDetails = {
            rentFour: "",
            depositFour: "",
            amenities: []
          };
        }

        return {
          selectedRoomTypes: [...currentSelected, roomTypeId],
          roomDetails: {
            ...prev.roomDetails,
            [roomTypeId]: initialDetails
          }
        };
      }
    });
    setTouched(prev => ({ ...prev, selectedRoomTypes: true }));
  }, [roomTypes]);

  const toggleRoomAmenity = useCallback((roomTypeId, amenityId) => {
    setForm(prev => {
      const currentAmenities = prev.roomDetails[roomTypeId]?.amenities || [];
      const newAmenities = currentAmenities.includes(amenityId)
        ? currentAmenities.filter(id => id !== amenityId)
        : [...currentAmenities, amenityId];

      return {
        ...prev,
        roomDetails: {
          ...prev.roomDetails,
          [roomTypeId]: {
            ...prev.roomDetails[roomTypeId],
            amenities: newAmenities
          }
        }
      };
    });
  }, []);

  const updateRoomDetailField = useCallback((roomTypeId, fieldName, value) => {
    setForm(prev => ({
      ...prev,
      roomDetails: {
        ...prev.roomDetails,
        [roomTypeId]: {
          ...prev.roomDetails[roomTypeId],
          [fieldName]: value
        }
      }
    }));
    const fieldKey = `${roomTypeId}.${fieldName}`;
    setTouched(prev => ({ ...prev, [fieldKey]: true }));
  }, []);

  // ============== DATA HYDRATION ==============
  const parseSelectedRoomTypes = useCallback((roomTypesData) => {
    if (!roomTypesData) return [];
    
    if (typeof roomTypesData === 'string') {
      return roomTypesData.split(',').map(id => String(id.trim())).filter(Boolean);
    }
    
    if (Array.isArray(roomTypesData)) {
      return roomTypesData.map(id => String(id));
    }
    
    return [String(roomTypesData)];
  }, []);

  const parseAmenities = useCallback((amenitiesData) => {
    if (!amenitiesData) return [];
    
    if (typeof amenitiesData === 'string') {
      return amenitiesData.split(',').map(id => String(id.trim())).filter(Boolean);
    }
    
    if (Array.isArray(amenitiesData)) {
      return amenitiesData.map(id => String(id));
    }
    
    return [String(amenitiesData)];
  }, []);

  const getRoomTypeCategory = useCallback((roomType) => {
    const name = roomType?.name?.toLowerCase() || "";
    if (name.includes("single")) return "single";
    if (name.includes("double")) return "double";
    if (name.includes("three")) return "three";
    if (name.includes("four")) return "four";
    return "other";
  }, []);

const hydrateRoomDetails = useCallback((attributes, selectedRoomTypeIds) => {
  const roomDetails = {};

  selectedRoomTypeIds.forEach(roomTypeId => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const category = getRoomTypeCategory(roomType);

    const amenitiesData =
      data?.amenities?.room_amenities ||
      attributes?.room_amenities ||
      [];

    let details = {
      amenities: parseAmenities(amenitiesData),
    };

    switch(category) {
      case "single":
        details = {
          ...details,
          rent: attributes.room_expected_rent
            ? String(attributes.room_expected_rent)
            : "",
          deposit: attributes.room_expected_deposit
            ? String(attributes.room_expected_deposit)
            : ""
        };
        break;

      case "double":
        details = {
          ...details,
          rentPerPerson: attributes.room_expected_rent_per_person
            ? String(attributes.room_expected_rent_per_person)
            : "",
          depositPerPerson: attributes.room_expected_deposit_per_person
            ? String(attributes.room_expected_deposit_per_person)
            : ""
        };
        break;

      case "three":
        details = {
          ...details,
          rentThree: attributes.room_expected_rent_three
            ? String(attributes.room_expected_rent_three)
            : "",
          depositThree: attributes.room_expected_deposit_three
            ? String(attributes.room_expected_deposit_three)
            : ""
        };
        break;

      case "four":
        details = {
          ...details,
          rentFour: attributes.room_expected_rent_four
            ? String(attributes.room_expected_rent_four)
            : "",
          depositFour: attributes.room_expected_deposit_four
            ? String(attributes.room_expected_deposit_four)
            : ""
        };
        break;

      default:
        break;
    }

    roomDetails[roomTypeId] = details;
  });

  return roomDetails;
}, [roomTypes, parseAmenities, getRoomTypeCategory, data]);

  // Effect to hydrate form when data changes
  useEffect(() => {
    if (!data || attributeValues.length === 0 || roomTypes.length === 0) {
      setIsInitializing(false);
      return;
    }

    const attributes = data.attributes || {};
    const selectedRoomTypeIds = parseSelectedRoomTypes(attributes.room_available_room_types)
      .filter(id => roomTypes.some(rt => rt.id === id));

    const newForm = {
      selectedRoomTypes: selectedRoomTypeIds,
      roomDetails: hydrateRoomDetails(attributes, selectedRoomTypeIds)
    };

    setForm(newForm);
    setIsInitializing(false);
  }, [data, attributeValues, roomTypes, parseSelectedRoomTypes, hydrateRoomDetails]);

  // ============== SAVE FUNCTION ==============
  const prepareAttributes = useCallback(() => {
    const attributes = {};
    
    if (form.selectedRoomTypes.length === 0) return attributes;
    
    attributes.room_available_room_types = form.selectedRoomTypes.map(Number);
    
    // Process each room type
    form.selectedRoomTypes.forEach(roomTypeId => {
      const roomType = roomTypes.find(rt => rt.id === roomTypeId);
      const category = getRoomTypeCategory(roomType);
      const details = form.roomDetails[roomTypeId] || {};
      
      switch(category) {
        case "single":
          if (details.rent) {
            attributes.room_expected_rent = Number(details.rent);
          }
          if (details.deposit) {
            attributes.room_expected_deposit = Number(details.deposit);
          }
          break;
        case "double":
          if (details.rentPerPerson) {
            attributes.room_expected_rent_per_person = Number(details.rentPerPerson);
          }
          if (details.depositPerPerson) {
            attributes.room_expected_deposit_per_person = Number(details.depositPerPerson);
          }
          break;
        case "three":
          if (details.rentThree) {
            attributes.room_expected_rent_three = Number(details.rentThree);
          }
          if (details.depositThree) {
            attributes.room_expected_deposit_three = Number(details.depositThree);
          }
          break;
        case "four":
          if (details.rentFour) {
            attributes.room_expected_rent_four = Number(details.rentFour);
          }
          if (details.depositFour) {
            attributes.room_expected_deposit_four = Number(details.depositFour);
          }
          break;
        default:
          break;
      }
    });
    
    // Collect unique amenities
    const allAmenities = new Set();
    form.selectedRoomTypes.forEach(rtId => {
      const amenities = form.roomDetails[rtId]?.amenities || [];
      amenities.forEach(amenity => allAmenities.add(amenity));
    });
    
    if (allAmenities.size > 0) {
attributes.room_amenities = Array.from(allAmenities).map(Number);    }

    return attributes;
  }, [form, roomTypes, getRoomTypeCategory]);

  const handleSave = useCallback(
    async (navigateNext = false) => {
      // Mark all fields as touched
      const allTouched = { selectedRoomTypes: true };
      
      form.selectedRoomTypes.forEach(roomTypeId => {
        const roomType = roomTypes.find(rt => rt.id === roomTypeId);
        const category = getRoomTypeCategory(roomType);
        
        switch(category) {
          case "single":
            allTouched[`${roomTypeId}.rent`] = true;
            allTouched[`${roomTypeId}.deposit`] = true;
            break;
          case "double":
            allTouched[`${roomTypeId}.rentPerPerson`] = true;
            allTouched[`${roomTypeId}.depositPerPerson`] = true;
            break;
          case "three":
            allTouched[`${roomTypeId}.rentThree`] = true;
            allTouched[`${roomTypeId}.depositThree`] = true;
            break;
          case "four":
            allTouched[`${roomTypeId}.rentFour`] = true;
            allTouched[`${roomTypeId}.depositFour`] = true;
            break;
          default:
            break;
        }
      });
      setTouched(allTouched);

      const validationError = validateForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");  

      try {
        let uuid = propertyUuid;

        if (!uuid && createProperty) {
          uuid = await createProperty();
        }

        const attributes = prepareAttributes();
        
        if (saveSectionAttributes) {
          await saveSectionAttributes(uuid, attributes, form, "room_details");
        }

        const updatedData = {
          ...data,
          attributes: {
            ...(data.attributes || {}),
            ...attributes
          }
        };

        onSaveSuccess?.(uuid, updatedData, currentStep);
        onComplete?.(form);

          setSuccess("Room details saved successfully!");
  setToastKey((k) => k + 1);


        if (navigateNext) {
          onSaved?.(uuid, form);
          onNavigateNext?.(form);
        }
      } catch (err) {
        console.error('Save failed:', err);
        setError(err.response?.data?.message || "Failed to save room details");
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      validateForm,
      propertyUuid,
      createProperty,
      saveSectionAttributes,
      onSaveSuccess,
      onComplete,
      onSaved,
      onNavigateNext,
      roomTypes,
      prepareAttributes,
      data,
      currentStep,
      getRoomTypeCategory
    ]
  );

  // ============== RENDER HELPERS ==============
  const getRoomTypeName = useCallback((roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType ? roomType.name : roomTypeId;
  }, [roomTypes]);

  const orderedSelectedRoomTypes = useMemo(() => 
    roomTypes
      .map(rt => rt.id)
      .filter(id => form.selectedRoomTypes.includes(id)),
    [roomTypes, form.selectedRoomTypes]
  );

  const renderRoomDetailFields = useCallback((roomTypeId) => {
    const details = form.roomDetails[roomTypeId] || {};
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const category = getRoomTypeCategory(roomType);
    
    let rentField = "";
    let depositField = "";
    let rentValue = "";
    let depositValue = "";
    let rentPlaceholder = "";
    let depositPlaceholder = "";
    let rentLabel = "";
    let depositLabel = "";

    switch(category) {
      case "single":
        rentField = "rent";
        depositField = "deposit";
        rentValue = details.rent || "";
        depositValue = details.deposit || "";
        rentPlaceholder = "Enter rent amount";
        depositPlaceholder = "Enter deposit amount";
        rentLabel = "Expected Rent (₹)";
        depositLabel = "Expected Deposit (₹)";
        break;
      case "double":
        rentField = "rentPerPerson";
        depositField = "depositPerPerson";
        rentValue = details.rentPerPerson || "";
        depositValue = details.depositPerPerson || "";
        rentPlaceholder = "Enter rent per person";
        depositPlaceholder = "Enter deposit per person";
        rentLabel = "Expected Rent per person (₹)";
        depositLabel = "Expected Deposit per person (₹)";
        break;
      case "three":
        rentField = "rentThree";
        depositField = "depositThree";
        rentValue = details.rentThree || "";
        depositValue = details.depositThree || "";
        rentPlaceholder = "Enter rent amount";
        depositPlaceholder = "Enter deposit amount";
        rentLabel = "Expected Rent (₹)";
        depositLabel = "Expected Deposit (₹)";
        break;
      case "four":
        rentField = "rentFour";
        depositField = "depositFour";
        rentValue = details.rentFour || "";
        depositValue = details.depositFour || "";
        rentPlaceholder = "Enter rent amount";
        depositPlaceholder = "Enter deposit amount";
        rentLabel = "Expected Rent (₹)";
        depositLabel = "Expected Deposit (₹)";
        break;
      default:
        return null;
    }

    const rentFieldKey = `${roomTypeId}.${rentField}`;
    const depositFieldKey = `${roomTypeId}.${depositField}`;

    const rentError = touched[rentFieldKey] ? getFieldError(rentFieldKey) : null;
    const depositError = touched[depositFieldKey] ? getFieldError(depositFieldKey) : null;

    return (
      <Stack gap={6}>
        <Grid>
          <Column lg={8}>
            <div>
              <label className="cds--label">
                {requiredLabel(rentLabel)}
              </label>
              <input
                type="number"
                className={`cds--text-input ${rentError ? 'cds--text-input--invalid' : ''}`}
                value={rentValue}
                onChange={(e) => updateRoomDetailField(roomTypeId, rentField, e.target.value)}
                onBlur={() => handleBlur(rentFieldKey)}
                placeholder={rentPlaceholder}
                disabled={saving}
     
                
                style={{ width: '100%' }}
              />
              {rentError && (
                <div className="cds--form-requirement">{rentError}</div>
              )}
            </div>
          </Column>
          <Column lg={8}>
            <div>
              <label className="cds--label">
                {requiredLabel(depositLabel)}
              </label>
              <input
                type="number"
                className={`cds--text-input ${depositError ? 'cds--text-input--invalid' : ''}`}
                value={depositValue}
                onChange={(e) => updateRoomDetailField(roomTypeId, depositField, e.target.value)}
                onBlur={() => handleBlur(depositFieldKey)}
                placeholder={depositPlaceholder}
                disabled={saving}
 
                
                style={{ width: '100%' }}
              />
              {depositError && (
                <div className="cds--form-requirement">{depositError}</div>
              )}
            </div>
          </Column>
        </Grid>

        {amenitiesOptions.length > 0 && (
          <div>
            <p style={{ fontWeight: 600, marginBottom: '1rem' }}>Room Amenities</p>
            <Grid>
              {amenitiesOptions.map((amenity) => (
                <Column key={amenity.id} sm={2} md={4} lg={4} style={{ marginBottom: '0.5rem' }}>
                  <Checkbox
                    id={`amenity-${roomTypeId}-${amenity.id}`}
                    labelText={amenity.name}
                    checked={details.amenities?.includes(amenity.id) || false}
                    onChange={() => toggleRoomAmenity(roomTypeId, amenity.id)}
                    disabled={saving}
                  />
                </Column>
              ))}
            </Grid>
          </div>
        )}
      </Stack>
    );
  }, [form.roomDetails, amenitiesOptions, saving, roomTypes, toggleRoomAmenity, touched, getFieldError, handleBlur, updateRoomDetailField, getRoomTypeCategory]);

  const panelStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '0.5rem',
    backgroundColor: '#ffffff',
    padding: '1.5rem',
    marginTop: '1rem'
  };

  // ============== RENDER ==============
  if (isInitializing && attributeValues.length === 0) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <InlineLoading status="active" description="Loading room details..." />
      </div>
    );
  }

  // Check if room types are available
  if (roomTypes.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <ToastNotification
          kind="info"
          title="No Room Types Available"
          subtitle="Please configure room types in the system settings."
          timeout={0}
        />
      </div>
    );
  }

  return (
    <>
      {error && (
        <ToastNotification
        key={`err-${toastKey}`}
          kind="error"
          title="Validation Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}
      
      {success && (
  <ToastNotification
    key={`ok-${toastKey}`}                             // 👈 IMPORTANT
    kind="success"
    title="Success"
    subtitle={success}
    lowContrast
    timeout={3000}
    onClose={() => setSuccess("")}
    style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
  />
)}

      <div>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>


              <p
                style={{
                  color: "#0f62fe",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  maxWidth: "700px",
                  margin: "0 auto",
                  lineHeight: "1.5",
                }}
              >
                Provide details about your place to find tenants soon.
              </p>
            </div>              <Stack gap={8}>
            <FormGroup legendText={requiredLabel("Select Available Room Types")}>
                  <Grid>
                    {roomTypes.map((roomType) => (
                      <Column key={roomType.id} lg={4} style={{ marginBottom: '1rem' }}>
                        <Checkbox
                          id={`room-type-${roomType.id}`}
                          labelText={roomType.name}
                          checked={form.selectedRoomTypes.includes(roomType.id)}
                          onChange={() => toggleRoomType(roomType.id)}
                          disabled={saving}
                        />
                      </Column>
                    ))}
                  </Grid>
                  {touched.selectedRoomTypes && !form.selectedRoomTypes?.length && (
                    <div className="cds--form-requirement" style={{ marginTop: '0.5rem' }}>
                      {validationRules.selectedRoomTypes.message}
                    </div>
                  )}
                </FormGroup>

                {form.selectedRoomTypes.length > 0 && (
                  <FormGroup legendText="Room Details">
                    {orderedSelectedRoomTypes.length >= 2 ? (
                      <Tabs 
                        selectedIndex={activeTabIndex} 
                        onChange={({ selectedIndex }) => setActiveTabIndex(selectedIndex)}
                      >
                        <TabList aria-label="Selected room types">
                          {orderedSelectedRoomTypes.map((roomTypeId) => (
                            <Tab key={roomTypeId}>{getRoomTypeName(roomTypeId)}</Tab>
                          ))}
                        </TabList>
                        <TabPanels>
                          {orderedSelectedRoomTypes.map((roomTypeId) => (
                            <TabPanel key={roomTypeId}>
                              <div style={panelStyle}>
                                <h5 style={{ margin: '0 0 1.5rem 0' }}>
                                  {getRoomTypeName(roomTypeId)} Room Details
                                </h5>
                                {renderRoomDetailFields(roomTypeId)}
                              </div>
                            </TabPanel>
                          ))}
                        </TabPanels>
                      </Tabs>
                    ) : (
                      <Accordion>
                        {orderedSelectedRoomTypes.map((roomTypeId) => (
                          <AccordionItem
                            key={roomTypeId}
                            title={`${getRoomTypeName(roomTypeId)} Room Details`}
                            open={true}
                          >
                            {renderRoomDetailFields(roomTypeId)}
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </FormGroup>
                )}

                {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}
                    onNext={() => handleSave(true)}
                    showSave
                    showNext
                    isSaveLoading={saving}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default RoomDetails;